/**
 * Advisor onboarding tour — epoch-aware "seen" tracking.
 *
 * We store BOTH a boolean seen flag and the `last_premium_upgraded_at`
 * timestamp the flag was set at. When the server reports a newer
 * timestamp (i.e. the user downgraded and then re-upgraded to Premium),
 * we treat the tour as unseen so the coach mark replays and the user
 * can rediscover the portfolio-aware capabilities they've unlocked.
 *
 * Storage keys:
 *   rp_advisor_tour_seen_v1       — legacy boolean flag ("1" == seen)
 *   rp_advisor_tour_seen_epoch_v1 — ISO timestamp we last marked seen at
 */
const SEEN_KEY = 'rp_advisor_tour_seen_v1';
const EPOCH_KEY = 'rp_advisor_tour_seen_epoch_v1';

function _readEpoch() {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(EPOCH_KEY) || null;
  } catch {
    return null;
  }
}

/**
 * Has the user seen the tour for the current Premium subscription
 * epoch? Returns true only when:
 *   - the legacy seen flag is "1", AND
 *   - the stored epoch is >= the server's `lastPremiumUpgradedAt`
 * If the server has a newer upgrade timestamp than we recorded when we
 * marked seen, replay the tour.
 */
export function hasSeenAdvisorTour(lastPremiumUpgradedAt = null) {
  if (typeof window === 'undefined') return true; // SSR-safe
  try {
    const seen = window.localStorage.getItem(SEEN_KEY) === '1';
    if (!seen) return false;
    if (!lastPremiumUpgradedAt) return true; // No epoch info → trust flag
    const stored = _readEpoch();
    if (!stored) return false; // We saw the tour before we tracked epochs → replay
    // Compare ISO strings lexicographically (same-timezone ISO-8601 sorts correctly)
    return stored >= lastPremiumUpgradedAt;
  } catch {
    return true;
  }
}

/**
 * Mark the tour seen at the current server epoch so we know to replay
 * only when a NEWER upgrade timestamp arrives.
 */
export function markAdvisorTourSeen(lastPremiumUpgradedAt = null) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(SEEN_KEY, '1');
    if (lastPremiumUpgradedAt) {
      window.localStorage.setItem(EPOCH_KEY, lastPremiumUpgradedAt);
    } else {
      window.localStorage.setItem(EPOCH_KEY, new Date().toISOString());
    }
  } catch {
    // no-op
  }
}

export function resetAdvisorTour() {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(SEEN_KEY);
    window.localStorage.removeItem(EPOCH_KEY);
  } catch {
    // no-op
  }
}
